import React, { useEffect, useState, useCallback } from 'react'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useNavigate } from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

import LifeLogo from 'Components/LifeLogo'
import warning from '../../Components/images/warning.png'
import mailicon from '../../Components/images/mail.png'
import LanguageSelector from 'Components/LanguageSelector' // LAP-733 (GE022)

import { makePostRequest } from 'utils/api'
import urls from 'utils/urls'
import { parseAuthError, AUTH_ERROR_CODES } from 'utils/authErrors'

/**
 * État de la machine d'affichage. Un seul état = un seul rendu possible.
 * Remplace l'ancienne combinaison error401 / resetPass / sendVerify
 * qui rendait la page imprévisible.
 */
const VIEW = Object.freeze({
  LOGIN: 'LOGIN',                       // formulaire de connexion (défaut)
  RESET_REQUEST: 'RESET_REQUEST',       // formulaire "mot de passe oublié"
  RESET_SENT: 'RESET_SENT',             // confirmation : email envoyé
  NEEDS_VERIFICATION: 'NEEDS_VERIFICATION', // l'email n'est pas vérifié
  VERIFICATION_SENT: 'VERIFICATION_SENT',   // confirmation : email de vérif renvoyé
})

const FOOTER_YEAR_START = 2024

export default function Login() {
  const navigate = useNavigate()
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  const [view, setView] = useState(VIEW.LOGIN)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)              // { code, message } | null
  const [showPassword, setShowPassword] = useState(false)

  // On retient le dernier email/password tapés pour pouvoir relancer
  // un envoi d'email de vérification sans redemander le mot de passe.
  const [lastCredentials, setLastCredentials] = useState({ email: '', password: '' })

  // Toast "session expirée" : déclenché uniquement par l'intercepteur axios
  // sur une vraie 401 hors routes d'auth (cf. utils/api.js).
  useEffect(() => {
    if (localStorage.getItem('login-again')) {
      toast.error(t('auth.login.sessionExpired'))
      localStorage.removeItem('login-again')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Réinitialise erreurs et bascule de vue. */
  const goTo = useCallback((nextView) => {
    setError(null)
    setView(nextView)
  }, [])

  /* ------------------------------------------------------------------
   *  Soumission : connexion
   * ------------------------------------------------------------------ */
  const handleLogin = async (event) => {
    event.preventDefault()
    if (submitting) return

    const data = new FormData(event.currentTarget)
    const payload = {
      email: (data.get('email') || '').trim(),
      password: data.get('password') || '',
    }

    setLastCredentials(payload)
    setError(null)
    setSubmitting(true)

    try {
      const response = await makePostRequest(urls.login, payload, {}, false)

      if (response?.status === 200 && response.data?.token) {
        localStorage.setItem('token', response.data.token)
        navigate('/')
        return
      }

      // Email non vérifié : aiguillage spécifique (vue dédiée).
      const parsed = parseAuthError(response, 'login')
      if (parsed.code === AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED) {
        localStorage.removeItem('token')
        setError(null)
        setView(VIEW.NEEDS_VERIFICATION)
        return
      }

      // Tous les autres cas : on affiche l'erreur en place.
      localStorage.removeItem('token')
      setError(parsed)
    } finally {
      setSubmitting(false)
    }
  }

  /* ------------------------------------------------------------------
   *  Soumission : demande de réinitialisation de mot de passe
   * ------------------------------------------------------------------ */
  const handleResetRequest = async (event) => {
    event.preventDefault()
    if (submitting) return

    const data = new FormData(event.currentTarget)
    const payload = { email: (data.get('email') || '').trim() }

    setError(null)
    setSubmitting(true)

    try {
      const response = await makePostRequest(
        '/api/user/verify-mail-password/',
        payload,
        {},
        false,
      )

      // Pour des raisons de sécurité (anti-énumération), on traite
      // 200 ET 404 comme un succès côté UI : impossible pour un
      // attaquant de deviner si l'email existe.
      if (response?.status === 200 || response?.status === 404) {
        setView(VIEW.RESET_SENT)
        return
      }

      setError(parseAuthError(response, 'reset'))
    } finally {
      setSubmitting(false)
    }
  }

  /* ------------------------------------------------------------------
   *  Action : renvoyer un email de vérification
   * ------------------------------------------------------------------ */
  const handleResendVerification = async () => {
    if (submitting) return
    if (!lastCredentials.email || !lastCredentials.password) {
      // Pas de contexte (rechargement) → on renvoie l'utilisateur au login
      goTo(VIEW.LOGIN)
      return
    }

    setError(null)
    setSubmitting(true)

    try {
      const response = await makePostRequest(
        '/api/user/verify-mail/',
        lastCredentials,
        {},
        false,
      )

      if (response?.status === 200) {
        setView(VIEW.VERIFICATION_SENT)
      } else {
        setError(parseAuthError(response, 'login'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  /* ------------------------------------------------------------------
   *  Sous-rendus
   * ------------------------------------------------------------------ */
  const renderError = () =>
    error && (
      <Grid item xs={12}>
        <Box
          role="alert"
          sx={errorBoxSx}
        >
          {error.message}
        </Box>
      </Grid>
    )

  const renderLoginForm = () => (
    <Box component="form" onSubmit={handleLogin} sx={formSx} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={formTitleSx}>{t('auth.login.title')}</Box>
          <Box sx={formSubtitleSx}>{t('auth.login.subtitle')}</Box>
        </Grid>

        {renderError()}

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="email"
            label={t('auth.login.email')}
            name="email"
            type="email"
            autoComplete="email"
            size="small"
            variant="outlined"
            error={!!error}
            inputProps={{ 'aria-label': t('auth.login.email') }}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="password"
            name="password"
            label={t('auth.login.password')}
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            size="small"
            variant="outlined"
            error={!!error}
            inputProps={{ 'aria-label': t('auth.login.password') }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                    edge="end"
                    size="small"
                    sx={{ color: '#2e3447' }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={textFieldSx}
          />
        </Grid>
      </Grid>

      <LoadingButton
        type="submit"
        loading={submitting}
        loadingPosition="start"
        startIcon={<span aria-hidden style={{ width: 0 }} />}
        size="large"
        variant="contained"
        fullWidth
        sx={primaryButtonSx}
      >
        <span>{t('auth.login.submit')}</span>
      </LoadingButton>

      <Box sx={formLinksSx}>
        <Box
          onClick={() => navigate('/register')}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/register')}
          sx={secondaryLinkSx}
        >
          {t('auth.login.createAccount')}
        </Box>
        <Box
          onClick={() => goTo(VIEW.RESET_REQUEST)}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goTo(VIEW.RESET_REQUEST)}
          sx={secondaryLinkSx}
        >
          {t('auth.login.forgotPassword')}
        </Box>
      </Box>
    </Box>
  )

  const renderResetRequest = () => (
    <Box component="form" onSubmit={handleResetRequest} sx={formSx} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={formTitleSx}>{t('auth.reset.title')}</Box>
          <Box sx={formSubtitleSx}>{t('auth.reset.subtitle')}</Box>
        </Grid>

        {renderError()}

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="reset-email"
            name="email"
            type="email"
            label={t('auth.login.email')}
            autoComplete="email"
            size="small"
            variant="outlined"
            error={!!error}
            sx={textFieldSx}
          />
        </Grid>
      </Grid>

      <LoadingButton
        type="submit"
        loading={submitting}
        loadingPosition="start"
        startIcon={<span aria-hidden style={{ width: 0 }} />}
        size="large"
        variant="contained"
        fullWidth
        sx={primaryButtonSx}
      >
        <span>{t('auth.reset.submit')}</span>
      </LoadingButton>

      <Box
        onClick={() => goTo(VIEW.LOGIN)}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goTo(VIEW.LOGIN)}
        sx={secondaryLinkSx}
      >
        {t('auth.reset.back')}
      </Box>
    </Box>
  )

  const renderResetSent = () => (
    <Box sx={messageViewSx}>
      <Box sx={{ mb: 3 }}>
        <img src={mailicon} alt="" />
      </Box>
      <Box sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', mb: 3, maxWidth: 360 }}>
        {t('auth.reset.sentMessage')}
      </Box>
      <Box
        onClick={() => goTo(VIEW.LOGIN)}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goTo(VIEW.LOGIN)}
        sx={secondaryLinkSx}
      >
        {t('auth.reset.loginAgain')}
      </Box>
    </Box>
  )

  const renderNeedsVerification = () => (
    <Box sx={messageViewSx}>
      <Box sx={{ mb: 4 }}>
        <img src={warning} alt="" />
      </Box>
      <Box sx={{ fontWeight: 700, fontSize: 18, textAlign: 'center', maxWidth: 360 }}>
        {t('auth.verification.needs')}
      </Box>

      {error && (
        <Box mt={2} sx={{ color: '#ff6b6b', fontSize: 13, textAlign: 'center' }}>
          {error.message}
        </Box>
      )}

      <LoadingButton
        onClick={handleResendVerification}
        loading={submitting}
        loadingPosition="start"
        startIcon={<span aria-hidden style={{ width: 0 }} />}
        variant="text"
        sx={textButtonSx}
      >
        {t('auth.verification.resend')}
      </LoadingButton>

      <Box
        onClick={() => goTo(VIEW.LOGIN)}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goTo(VIEW.LOGIN)}
        mt={1}
        sx={secondaryLinkSx}
      >
        {t('auth.reset.loginAgain')}
      </Box>
    </Box>
  )

  const renderVerificationSent = () => (
    <Box sx={messageViewSx}>
      <Box sx={{ mb: 3 }}>
        <img src={mailicon} alt="" />
      </Box>
      <Box sx={{ fontWeight: 700, fontSize: 14, textAlign: 'center', mb: 3, maxWidth: 360 }}>
        {t('auth.verification.sent')}
      </Box>
      <Box
        onClick={() => goTo(VIEW.LOGIN)}
        role="link"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && goTo(VIEW.LOGIN)}
        sx={secondaryLinkSx}
      >
        {t('auth.reset.loginAgain')}
      </Box>
    </Box>
  )

  /* ------------------------------------------------------------------
   *  Aiguillage
   * ------------------------------------------------------------------ */
  const renderActiveView = () => {
    switch (view) {
      case VIEW.RESET_REQUEST:        return renderResetRequest()
      case VIEW.RESET_SENT:           return renderResetSent()
      case VIEW.NEEDS_VERIFICATION:   return renderNeedsVerification()
      case VIEW.VERIFICATION_SENT:    return renderVerificationSent()
      case VIEW.LOGIN:
      default:                        return renderLoginForm()
    }
  }

  const currentYear = new Date().getFullYear()
  const footerYears =
    currentYear > FOOTER_YEAR_START ? `${FOOTER_YEAR_START} - ${currentYear}` : `${FOOTER_YEAR_START}`

  return (
    <Box sx={pageSx}>
      <Box sx={overlaySx} />
      <Box sx={headerSx}>
        <LifeLogo style={{ height: 48, width: 'auto', display: 'block' }} />
        {/* LAP-733 (GE022) — language picker on auth pages, no persist */}
        <Box sx={headerLangSx}>
          <LanguageSelector compact disablePersist />
        </Box>
      </Box>
      <Box sx={shellSx}>
        <Box sx={heroGridSx}>
          <Box sx={welcomeSx}>
            <Box sx={eyebrowSx}>{t('auth.hero.eyebrow')}</Box>
            <Box component="h1" sx={heroTitleSx}>
              {t('auth.hero.title')}
            </Box>
            <Box sx={heroTextSx}>{t('auth.hero.subtitle')}</Box>
          </Box>

          <Box sx={panelSx}>
            {renderActiveView()}
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box>
            {t('auth.footer.visitUs')}{' '}
            <Box
              component="a"
              href="https://be-lifer.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={footerLinkSx}
            >
              Be-lifer.com
            </Box>
          </Box>
          <Box>© {footerYears} Life. {t('auth.footer.rightsReserved')}</Box>
        </Box>
      </Box>
    </Box>
  )
}

const pageSx = {
  minHeight: '100vh',
  position: 'relative',
  overflow: 'hidden auto',
  color: 'white',
  background:
    'linear-gradient(135deg, #020417 0%, #071338 48%, #102b4f 100%)',
  '&:before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(circle at 14% 22%, rgba(74, 178, 255, 0.26) 0, rgba(74, 178, 255, 0) 30%), radial-gradient(circle at 88% 12%, rgba(21, 214, 181, 0.18) 0, rgba(21, 214, 181, 0) 26%), linear-gradient(115deg, rgba(255,255,255,0.07) 0 1px, transparent 1px 100%)',
    backgroundSize: 'auto, auto, 84px 84px',
    pointerEvents: 'none',
  },
}

const overlaySx = {
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(90deg, rgba(1, 3, 29, 0.72) 0%, rgba(1, 3, 29, 0.32) 50%, rgba(1, 3, 29, 0.16) 100%)',
}

const headerSx = {
  position: 'relative',
  zIndex: 2,
  height: { xs: 82, md: 92 },
  px: { xs: 2.25, sm: 4, md: '7%' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between', // LAP-733 (GE022) — logo left / lang picker right
  background: 'rgba(0, 0, 0, 0.92)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 18px 45px rgba(0, 0, 0, 0.35)',
}

// LAP-733 (GE022) — visual treatment for the unauthenticated language picker.
const headerLangSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: 2,
    '& fieldset': { border: 'none' },
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.14)' },
    '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.16)' },
  },
  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.78)' },
}

const shellSx = {
  position: 'relative',
  zIndex: 1,
  minHeight: { xs: 'calc(100vh - 82px)', md: 'calc(100vh - 92px)' },
  px: { xs: 2.25, sm: 4, md: '7%' },
  py: { xs: 3, md: 4 },
  display: 'flex',
  flexDirection: 'column',
}

const heroGridSx = {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.05fr) minmax(380px, 460px)' },
  alignItems: 'center',
  gap: { xs: 4, md: 8 },
  py: { xs: 5, md: 6 },
}

const welcomeSx = {
  maxWidth: 620,
  display: { xs: 'none', md: 'block' },
}

const eyebrowSx = {
  display: 'inline-flex',
  px: 1.5,
  py: 0.75,
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.18)',
  backgroundColor: 'rgba(255,255,255,0.08)',
  color: 'rgba(255,255,255,0.84)',
  fontSize: 14,
  fontWeight: 800,
}

const heroTitleSx = {
  mt: 3,
  mb: 0,
  maxWidth: 620,
  color: '#f7fbff',
  fontSize: { md: 48, lg: 58 },
  lineHeight: 1.06,
  fontWeight: 900,
  letterSpacing: 0,
  textShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
}

const heroTextSx = {
  mt: 2.5,
  maxWidth: 520,
  color: 'rgba(255,255,255,0.78)',
  fontSize: 18,
  lineHeight: 1.65,
  fontWeight: 600,
}

const panelSx = {
  width: '100%',
  justifySelf: { xs: 'center', md: 'end' },
  p: { xs: 2.5, sm: 4 },
  borderRadius: 3,
  border: '1px solid rgba(255,255,255,0.14)',
  background: 'rgba(3, 7, 28, 0.84)',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.46)',
  backdropFilter: 'blur(18px)',
}

const formSx = {
  width: '100%',
}

const formTitleSx = {
  fontSize: { xs: 27, sm: 30 },
  lineHeight: 1.15,
  fontWeight: 900,
  letterSpacing: 0,
}

const formSubtitleSx = {
  mt: 1,
  color: 'rgba(255,255,255,0.72)',
  fontSize: 14.5,
  lineHeight: 1.55,
  fontWeight: 600,
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 50,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.96)',
    transition: 'box-shadow 160ms ease, background-color 160ms ease',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.22)' },
    '&:hover fieldset': { borderColor: '#79b7ff' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: '0 0 0 4px rgba(74, 178, 255, 0.18)',
    },
    '&.Mui-focused fieldset': { borderColor: '#4ab2ff', borderWidth: 1 },
    '&.Mui-error fieldset': { borderColor: '#ffb45c' },
  },
  '& .MuiInputBase-input': {
    color: '#070a1d',
    fontSize: 16,
    fontWeight: 600,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(7, 10, 29, 0.72)',
    fontWeight: 700,
  },
  '& .MuiInputLabel-shrink': {
    px: 0.75,
    borderRadius: 1,
    backgroundColor: '#f7f8fc',
    transform: 'translate(12px, -8px) scale(0.75)',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1d6fd1' },
  '& .MuiInputLabel-root.Mui-error': { color: '#ffb45c' },
}

const primaryButtonSx = {
  mt: 3,
  mb: 2,
  py: 1.45,
  borderRadius: 2,
  fontSize: 16,
  fontWeight: 800,
  letterSpacing: 0,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #2d8cff 0%, #1d6fd1 100%)',
  boxShadow: '0 18px 34px rgba(30, 126, 230, 0.32)',
  ':hover': {
    background: 'linear-gradient(135deg, #4aa0ff 0%, #2379dc 100%)',
    boxShadow: '0 20px 40px rgba(30, 126, 230, 0.4)',
  },
  '&.Mui-disabled': { color: 'white', backgroundColor: '#5a5a5a' },
}

const formLinksSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  justifyContent: 'space-between',
  gap: 1.5,
  mt: 1,
}

const secondaryLinkSx = {
  color: '#5bbcff',
  fontWeight: 800,
  cursor: 'pointer',
  textDecoration: 'none',
  ':hover': {
    color: '#8fd2ff',
    textDecoration: 'underline',
  },
}

const textButtonSx = {
  mt: 2,
  color: '#5bbcff',
  textDecoration: 'none',
  textTransform: 'none',
  fontWeight: 800,
  ':hover': {
    color: '#8fd2ff',
    textDecoration: 'underline',
  },
}

const errorBoxSx = {
  color: '#ffd2a6',
  backgroundColor: 'rgba(255, 180, 92, 0.1)',
  border: '1px solid rgba(255, 180, 92, 0.32)',
  borderRadius: 2,
  padding: '10px 12px',
  fontSize: 14,
  fontWeight: 700,
}

const messageViewSx = {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  mt: 2,
  textAlign: 'center',
}

const footerSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  justifyContent: 'space-between',
  gap: 1.5,
  color: 'rgba(255,255,255,0.62)',
  fontSize: 14,
  fontWeight: 600,
}

const footerLinkSx = {
  color: '#7cc7ff',
  cursor: 'pointer',
  textDecoration: 'none',
  fontWeight: 800,
  ':hover': { textDecoration: 'underline' },
}
